import { CircleCheck, CircleHelp, Clock, Loader, PlayCircle, RefreshCw, XCircle } from 'lucide-react'

import { Button, DiagnosticCopy } from '@krp/ui-kit'

import type { PaymentNoticeVm, RecoveryLineState, RecoveryPanelActions, RecoveryPanelVm } from '../port/types'
import { ReceiptVerdict } from './ReceiptVerdict'

/** Слово состояния строки — рядом со знаком: состояние различается не только цветом. */
const LINE_WORD: Readonly<Record<RecoveryLineState, string>> = {
  ok: 'подтверждено',
  problem: 'не прошло',
  waiting: 'ждём',
  unknown: 'не знаем',
}

function LineIcon({ state }: { state: RecoveryLineState }) {
  switch (state) {
    case 'ok':
      return <CircleCheck className="rmk-progress__step-icon" size={22} aria-hidden="true" />
    case 'problem':
      return <XCircle className="rmk-progress__step-icon" size={22} aria-hidden="true" />
    case 'waiting':
      return <Clock className="rmk-progress__step-icon" size={22} aria-hidden="true" />
    default:
      return <CircleHelp className="rmk-progress__step-icon" size={22} aria-hidden="true" />
  }
}

/** Отказ чтения — дословно, код и номер запроса рядом; не исчезает сам. */
function ReadError({ error }: { error: PaymentNoticeVm }) {
  return (
    <div className="rmk-progress__notice" data-tone={error.tone} role="alert">
      <p>
        <XCircle size={16} aria-hidden="true" /> <strong>{error.title}.</strong> {error.text}
      </p>
      {error.hint !== null && <p className="sale-text">{error.hint}</p>}
      {error.meta !== null && <p className="rmk-progress__details-line">{error.meta}</p>}
    </div>
  )
}

/**
 * Панель «Проверка в …» внутри окна оплаты (RP-CASH-RECOVERY §4.1): что нашёл источник (три строки со временем проверки), пояснение и ОДНО
 * основное действие. Кассир перечитывает состояние или выполняет действие — больше ничего не заполняет. Недоступное действие — словами
 * на месте кнопки; «Скопировать для разбора» — с ручным копированием, если буфер обмена недоступен.
 */
export function RecoveryPanel({ recovery, actions }: { recovery: RecoveryPanelVm; actions: RecoveryPanelActions }) {
  const primary = recovery.primary
  const verdict = recovery.verdict
  return (
    <section className="rmk-progress__resolve" aria-label={recovery.title}>
      <h3>{recovery.title}</h3>

      {recovery.progressText !== null && (
        <p className="sale-text" role="status" aria-live="polite">
          <Loader size={14} aria-hidden="true" /> {recovery.progressText}
        </p>
      )}

      {recovery.readError !== null && <ReadError error={recovery.readError} />}

      {recovery.stateChanged !== null && (
        <p className="rmk-progress__notice" data-tone="warning" role="alert">
          <CircleHelp size={16} aria-hidden="true" /> {recovery.stateChanged}
        </p>
      )}

      {recovery.lines.length > 0 && (
        <ol className="rmk-progress__steps" aria-label="Что нашли">
          {recovery.lines.map((line) => (
            <li key={line.key} className="rmk-progress__step" data-state={line.state === 'ok' ? 'done' : line.state === 'problem' ? 'failed' : line.state}>
              <LineIcon state={line.state} />
              <div>
                <p className="rmk-progress__step-title">
                  <span>{line.label}</span> — <strong>{LINE_WORD[line.state]}</strong>
                </p>
                <p className="rmk-progress__text">{line.text}</p>
                {line.checkedText !== null && <p className="rmk-progress__watch">{line.checkedText}</p>}
              </div>
            </li>
          ))}
        </ol>
      )}

      {recovery.explanation.map((line) => (
        <p key={line} className="sale-text">
          {line}
        </p>
      ))}

      {recovery.instructions.length > 0 && (
        <ol className="rmk-progress__instructions" aria-label="Что сделать на кассе">
          {recovery.instructions.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      )}

      {recovery.nextCheckText !== null && (
        <p className="sale-text" role="status">
          <Clock size={14} aria-hidden="true" /> {recovery.nextCheckText}
        </p>
      )}

      {primary !== null && (
        <div className="rmk-progress__main">
          <Button
            variant="primary"
            icon={primary.enabled ? <PlayCircle size={22} aria-hidden="true" /> : <Clock size={22} aria-hidden="true" />}
            disabled={!primary.enabled}
            busy={primary.busy}
            onClick={actions.runPrimary}
          >
            {primary.label}
          </Button>
          {primary.blockedReason !== null && (
            <p className="sale-text" role="status">
              {primary.blockedReason}
            </p>
          )}
        </div>
      )}

      {verdict !== null && actions.verdict !== undefined && <ReceiptVerdict verdict={verdict} actions={actions.verdict} />}

      <div className="rmk-progress__secondary">
        {recovery.canCheckAgain && (
          <Button icon={<RefreshCw size={16} aria-hidden="true" />} disabled={!recovery.canRefresh} onClick={actions.refresh}>
            Проверить ещё раз
          </Button>
        )}
        <Button variant="ghost" icon={<RefreshCw size={16} aria-hidden="true" />} disabled={!recovery.canRefresh} onClick={actions.refresh}>
          Актуализировать состояние
        </Button>
        <DiagnosticCopy text={recovery.copyText} label="Скопировать для разбора" />
      </div>
    </section>
  )
}
